import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const SuperAdminDashboardVM = () => {
  // Dashboard state
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalTerminals: 0,
    activeTerminals: 0,
    totalUsers: 0,
    totalVisits: 0,
    pendingVisits: 0,
    activeVisitors: 0
  });

  // Terminals state
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState("");
  const [terminalUsers, setTerminalUsers] = useState([]);
  const [terminalVisits, setTerminalVisits] = useState([]);

  // Countries data
  const [countries, setCountries] = useState([]);

  // Active tab management
  const [activeTab, setActiveTab] = useState("terminals");

  // Modal states
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // Form states - REMOVED USERNAME
  const [terminalForm, setTerminalForm] = useState({
    name: "",
    country: "",
    time_zone: "",
    status: 1
  });

  const [userForm, setUserForm] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
    role_id: "",
    terminal_id: "",
    active: true
  });

  // Search and filter states
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all");

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // User roles
  const [userRoles, setUserRoles] = useState([]);

  // Success/Error messages
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    data: null
  });

  const navigate = useNavigate();
  const { handleToastOpen } = useToast();

  // Check if user is super admin
  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "SuperAdmin") {
      navigate("/visitor-mgmt/login");
    }
  }, [navigate]);

  // Fetch initial data
  useEffect(() => {
    fetchDashboardData();
    fetchTerminals();
    fetchCountries();
    fetchUserRoles();
  }, []);

  // Fetch dashboard overview data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/dashboard`
      );
      
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      handleToastOpen("error", "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all terminals
  const fetchTerminals = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/terminals`
      );
      
      if (response.data.success) {
        setTerminals(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching terminals:", error);
      handleToastOpen("error", "Failed to load terminals");
    }
  };

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/countries`
      );
      
      if (response.data.success) {
        setCountries(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  // Fetch user roles
  const fetchUserRoles = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/user-roles`
      );
      
      if (response.data.success) {
        setUserRoles(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  };

  // Fetch terminal-specific data
  const fetchTerminalData = async (terminalId) => {
    if (!terminalId) return;
    
    setLoading(true);
    try {
      // Fetch users for terminal
      const usersResponse = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/terminals/${terminalId}/users`
      );
      
      if (usersResponse.data.success) {
        setTerminalUsers(usersResponse.data.data);
      }

      // Fetch visits for terminal
      const visitsResponse = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/terminals/${terminalId}/visits`
      );
      
      if (visitsResponse.data.success) {
        setTerminalVisits(visitsResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching terminal data:", error);
      handleToastOpen("error", "Failed to load terminal data");
    } finally {
      setLoading(false);
    }
  };

  // Handle terminal selection
  const handleTerminalChange = useCallback((terminalId) => {
    setSelectedTerminal(terminalId);
    if (terminalId) {
      fetchTerminalData(terminalId);
    } else {
      setTerminalUsers([]);
      setTerminalVisits([]);
    }
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSearchText("");
    setFilterStatus("all");
    setFilterRole("all");
  }, []);

  // Handle search - completely stable reference
  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []); // No dependencies at all

  // FIXED: Terminal form handlers - completely stable reference
  const handleTerminalFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setTerminalForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field - using functional update to avoid re-renders
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }, []); // No dependencies at all

  // FIXED: User form handlers - completely stable reference
  const handleUserFormChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setUserForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    
    // Clear validation error for this field - using functional update to avoid re-renders
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }, []); // No dependencies at all

  // Validate terminal form
  const validateTerminalForm = () => {
    const errors = {};
    
    if (!terminalForm.name.trim()) {
      errors.name = "Terminal name is required";
    }
    
    if (!terminalForm.country) {
      errors.country = "Country is required";
    }
    
    if (!terminalForm.time_zone.trim()) {
      errors.time_zone = "Time zone is required";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate user form - REMOVED USERNAME VALIDATION
  const validateUserForm = () => {
    const errors = {};
    
    if (!userForm.full_name || !userForm.full_name.trim()) {
      errors.full_name = "Full name is required";
    }
    
    if (!userForm.email || !userForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
      errors.email = "Invalid email format";
    }
    
    if (!userForm.mobile_number || !userForm.mobile_number.toString().trim()) {
      errors.mobile_number = "Mobile number is required";
    }
    
    if (!userForm.role_id) {
      errors.role_id = "Role is required";
    }
    
    if (!userForm.terminal_id) {
      errors.terminal_id = "Terminal is required";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Create/Update terminal
  const handleTerminalSubmit = async () => {
    if (!validateTerminalForm()) return;
    
    setLoading(true);
    try {
      const url = editingTerminal 
        ? `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/terminals/${editingTerminal.id}`
        : `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/terminals`;
      
      const method = editingTerminal ? 'put' : 'post';
      
      const response = await axios[method](url, terminalForm);
      
      if (response.data.success) {
        handleToastOpen("success", `Terminal ${editingTerminal ? 'updated' : 'created'} successfully`);
        setShowTerminalModal(false);
        resetTerminalForm();
        fetchTerminals();
        fetchDashboardData();
      }
    } catch (error) {
      console.error("Error saving terminal:", error);
      const message = error.response?.data?.message || "Failed to save terminal";
      handleToastOpen("error", message);
    } finally {
      setLoading(false);
    }
  };

  // Create/Update user
  const handleUserSubmit = async () => {
    if (!validateUserForm()) return;
    
    setLoading(true);
    try {
      const url = editingUser 
        ? `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/users/${editingUser.id}`
        : `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/users`;
      
      const method = editingUser ? 'put' : 'post';
      
      const response = await axios[method](url, userForm);
      
      if (response.data.success) {
        handleToastOpen("success", `User ${editingUser ? 'updated' : 'created'} successfully`);
        setShowUserModal(false);
        resetUserForm();
        if (selectedTerminal) {
          fetchTerminalData(selectedTerminal);
        }
        fetchDashboardData();
      }
    } catch (error) {
      console.error("Error saving user:", error);
      const message = error.response?.data?.message || "Failed to save user";
      handleToastOpen("error", message);
    } finally {
      setLoading(false);
    }
  };

  // Delete terminal
  const handleDeleteTerminal = async (terminal) => {
    setConfirmDialog({
      open: true,
      title: "Delete Terminal",
      message: `Are you sure you want to delete terminal "${terminal.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(
            `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/terminals/${terminal.id}`
          );
          
          handleToastOpen("success", "Terminal deleted successfully");
          fetchTerminals();
          fetchDashboardData();
          
          if (selectedTerminal === terminal.id.toString()) {
            setSelectedTerminal("");
            setTerminalUsers([]);
            setTerminalVisits([]);
          }
        } catch (error) {
          console.error("Error deleting terminal:", error);
          handleToastOpen("error", "Failed to delete terminal");
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      },
      data: terminal
    });
  };

  // Delete user
  const handleDeleteUser = async (user) => {
    setConfirmDialog({
      open: true,
      title: "Delete User",
      message: `Are you sure you want to delete user "${user.full_name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(
            `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/super-admin/users/${user.id}`
          );
          
          handleToastOpen("success", "User deleted successfully");
          if (selectedTerminal) {
            fetchTerminalData(selectedTerminal);
          }
          fetchDashboardData();
        } catch (error) {
          console.error("Error deleting user:", error);
          handleToastOpen("error", "Failed to delete user");
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      },
      data: user
    });
  };

  // Reset forms - REMOVED USERNAME
  const resetTerminalForm = () => {
    setTerminalForm({
      name: "",
      country: "",
      time_zone: "",
      status: 1
    });
    setEditingTerminal(null);
    setValidationErrors({});
  };

  const resetUserForm = () => {
    setUserForm({
      full_name: "",
      email: "",
      mobile_number: "",
      role_id: "",
      terminal_id: "",
      active: true
    });
    setEditingUser(null);
    setValidationErrors({});
  };

  // Open modals for editing
  const handleEditTerminal = (terminal) => {
    setEditingTerminal(terminal);
    setTerminalForm({
      name: terminal.name,
      country: terminal.country,
      time_zone: terminal.time_zone,
      status: terminal.status
    });
    setShowTerminalModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      full_name: user.full_name,
      email: user.email,
      mobile_number: user.mobile_number,
      role_id: user.role_id,
      terminal_id: user.terminal_id,
      active: user.active
    });
    setShowUserModal(true);
  };

  // Filtered data - REMOVED USERNAME REFERENCES
  const filteredTerminalUsers = useMemo(() => {
    if (!terminalUsers.length) return [];
    
    return terminalUsers.filter(user => {
      const matchesSearch = !searchText || 
        user.full_name.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase());
      
      const matchesStatus = filterStatus === "all" || 
        (filterStatus === "active" && user.active) ||
        (filterStatus === "inactive" && !user.active);
      
      const matchesRole = filterRole === "all" || 
        user.role_name.toLowerCase() === filterRole.toLowerCase();
      
      return matchesSearch && matchesStatus && matchesRole;
    });
  }, [terminalUsers, searchText, filterStatus, filterRole]);

  const filteredTerminalVisits = useMemo(() => {
    if (!terminalVisits.length) return [];
    
    return terminalVisits.filter(visit => {
      const matchesSearch = !searchText || 
        visit.visitor_name.toLowerCase().includes(searchText.toLowerCase()) ||
        visit.approver_name.toLowerCase().includes(searchText.toLowerCase()) ||
        visit.purpose.toLowerCase().includes(searchText.toLowerCase());
      
      return matchesSearch;
    });
  }, [terminalVisits, searchText]);

  // Clear alerts
  const closeAlerts = useCallback(() => {
    setSuccessMessage("");
    setErrorMessage("");
  }, []);

  return {
    // Dashboard state
    loading,
    dashboardData,
    
    // Terminals state
    terminals,
    selectedTerminal,
    terminalUsers,
    terminalVisits,
    
    // Countries and roles
    countries,
    userRoles,
    
    // Active tab
    activeTab,
    
    // Modal states
    showTerminalModal,
    showUserModal,
    editingTerminal,
    editingUser,
    
    // Form states
    terminalForm,
    userForm,
    
    // Search and filters
    searchText,
    filterStatus,
    filterRole,
    
    // Validation
    validationErrors,
    
    // Messages
    successMessage,
    errorMessage,
    
    // Confirmation dialog
    confirmDialog,
    setConfirmDialog,
    
    // Filtered data
    filteredTerminalUsers,
    filteredTerminalVisits,
    
    // Handlers
    handleTerminalChange,
    handleTabChange,
    handleSearchChange,
    handleTerminalFormChange,
    handleUserFormChange,
    handleTerminalSubmit,
    handleUserSubmit,
    handleDeleteTerminal,
    handleDeleteUser,
    handleEditTerminal,
    handleEditUser,
    
    // Modal handlers
    setShowTerminalModal,
    setShowUserModal,
    resetTerminalForm,
    resetUserForm,
    
    // Filter handlers
    setFilterStatus,
    setFilterRole,
    
    // Utilities
    closeAlerts,
    fetchDashboardData,
    fetchTerminals,
    fetchTerminalData
  };
};

export default SuperAdminDashboardVM;